import { FileStore } from "#/api/fileStore.type";
import { FileUpIcon } from "#/components/icons/file-up-icon";
import { AdditionalUploadData } from "#/api/storage.type";
import { FILE_UPLOAD_ACCEPT } from "#/lib/fileUpload";
import {
  ArrowPathIcon,
  DocumentPlusIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FileUploadContextWrapper,
  useFileUploadContext,
} from "../../hooks/FileUploadContext";
import { useLoadContacts } from "../../hooks/useLoadContacts";
import { useUserDetails } from "../../hooks/useUserDetails";
import { SubmitButton } from "../shared/buttons/submit-button";
import Typography from "../shared/typography";

type UploadDocumentButtonProps = {
  accept?: string;
  multiple?: boolean;
  onFilesSelected?: (files: File[]) => void;
  onUploadComplete?: (files: FileStore[]) => void;
};

const DEFAULT_ACCEPT = FILE_UPLOAD_ACCEPT;

function UploadDocumentButtonInner({
  accept = DEFAULT_ACCEPT,
  multiple = true,
  onFilesSelected,
  onUploadComplete,
}: UploadDocumentButtonProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [lastUploadedFiles, setLastUploadedFiles] = useState<FileStore[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const { userDetails } = useUserDetails();
  const { contacts } = useLoadContacts();
  const { userId: routeUserId } = useParams<{ userId?: string }>();
  const {
    files,
    setFiles,
    fileUploading,
    handleRemoveImage,
    fileUploadResponse,
    uploadFilesForChat,
    medicalRecordIngest,
  } = useFileUploadContext();

  const handlePickerClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelection = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files
      ? Array.from(event.target.files)
      : [];

    if (!selectedFiles.length) {
      return;
    }

    setFiles((prev) =>
      multiple ? [...prev, ...selectedFiles] : [...selectedFiles]
    );
    onFilesSelected?.(selectedFiles);

    // Allow re-selecting the same files consecutively.
    event.target.value = "";
  };

  const isUploadingForAnotherUser = Boolean(
    routeUserId && routeUserId !== userDetails?.id
  );

  const matchedContact = useMemo(() => {
    if (!routeUserId) return undefined;
    return contacts.find((contact) => contact.related_user_id === routeUserId);
  }, [contacts, routeUserId]);

  const uploadAdditionalData = useMemo<AdditionalUploadData | undefined>(() => {
    if (!isUploadingForAnotherUser || !routeUserId) {
      return undefined;
    }

    const payload: AdditionalUploadData = {
      purpose: "medical_record",
      subjectUserId: routeUserId,
      patientUserId: routeUserId,
      uploadedByUserId: userDetails?.id,
    };

    if (matchedContact?.id) {
      payload.contactId = matchedContact.id;
    }

    return payload;
  }, [isUploadingForAnotherUser, routeUserId, userDetails?.id, matchedContact]);

  const handleUpload = () => {
    if (!files.length) {
      return;
    }
    uploadFilesForChat({ files, additionalData: uploadAdditionalData });
    setIsProcessing(true);
  };

  const normalizeUploads = useMemo(
    () =>
      (
        stores?: (FileStore & { signed_url?: string | null })[] | null,
        ingest?: Array<Record<string, any>> | null
      ): FileStore[] => {
        const normalized: FileStore[] = [];

        (stores ?? []).forEach((file) => {
          normalized.push({
            ...file,
            url: (file.signed_url ?? file.url) || null,
          });
        });

          (ingest ?? []).forEach((entry) => {
            const fallbackTimestamp = entry.created_at || new Date().toISOString();
            normalized.push({
              ambient_conversation_id: entry.ambient_conversation_id ?? null,
              audio_transcript: entry.audio_transcript ?? null,
              created_at: entry.created_at ?? fallbackTimestamp,
              file_id: entry.file_id ?? null,
              id:
                entry.id ??
                entry.file_id ??
                (typeof crypto !== "undefined" && "randomUUID" in crypto
                  ? crypto.randomUUID()
                  : `temp-${Date.now()}-${Math.random()
                      .toString(36)
                      .slice(2, 8)}`),
            name: entry.name ?? "Uploaded document",
            notes: entry.notes ?? null,
            project_id: entry.project_id ?? null,
            purpose: entry.purpose ?? "medical_record",
            relation_id: entry.relation_id ?? null,
            type: entry.type ?? null,
            updated_at: entry.updated_at ?? fallbackTimestamp,
            url: entry.signedUrl ?? entry.signed_url ?? entry.url ?? null,
            user_id: entry.user_id ?? userDetails?.id ?? null,
            vector_store_id: entry.vector_store_id ?? null,
          });
        });

        return normalized;
      },
    [userDetails?.id]
  );

  useEffect(() => {
    let ingestArray: Array<Record<string, any>> = [];
    if (Array.isArray(medicalRecordIngest)) {
      ingestArray = medicalRecordIngest;
    } else if (medicalRecordIngest && Array.isArray(medicalRecordIngest.data)) {
      ingestArray = medicalRecordIngest.data as Array<Record<string, any>>;
    } else if (medicalRecordIngest?.data) {
      ingestArray = [medicalRecordIngest.data as Record<string, any>];
    } else if (medicalRecordIngest) {
      ingestArray = [medicalRecordIngest as unknown as Record<string, any>];
    }

    const hasUploads = Boolean(
      (fileUploadResponse && fileUploadResponse.length > 0) || ingestArray.length
    );

    if (!fileUploading && hasUploads) {
      const normalized = normalizeUploads(fileUploadResponse, ingestArray);
      onUploadComplete?.(normalized);
      setLastUploadedFiles(normalized);
      setIsProcessing(false);
      setFiles([]);
    }
  }, [
    fileUploading,
    fileUploadResponse,
    medicalRecordIngest,
    normalizeUploads,
    onUploadComplete,
    setFiles,
  ]);

  const formatFileName = useMemo(
    () =>
      (name: string) => {
        const maxLength = 36;
        if (name.length <= maxLength) {
          return name;
        }
        return `${name.slice(0, maxLength - 3)}...`;
      },
    []
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <SubmitButton
          size="sm"
          color="cream"
          onClick={handlePickerClick}
          isDisabled={fileUploading}
          hideArrow
          icon={<DocumentPlusIcon className="w-5 h-5 text-brown-800" />}
        >
          {fileUploading ? "Uploading…" : "Upload File"}
        </SubmitButton>
        {files.length > 0 && (
          <SubmitButton
            type="button"
            size="sm"
            color="terracotta"
            hideArrow
            onClick={handleUpload}
            isDisabled={fileUploading}
          >
            {fileUploading
              ? "Uploading"
              : `Submit ${files.length} file${files.length > 1 ? "s" : ""}`}
          </SubmitButton>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        onChange={handleFileSelection}
      />

      {files.length > 0 && (
        <div className="rounded-xl border border-beige-200 bg-cream-50 p-4">
          <Typography
            variant="caption"
            className="mb-2 block text-xs font-semibold uppercase tracking-wide text-brown-600"
          >
            Pending files
          </Typography>
          <ul className="space-y-3">
            {files.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center justify-between rounded-lg border border-beige-200 bg-white px-4 py-3 text-sm text-brown-800"
              >
                <span className="max-w-[15rem] truncate" title={file.name}>
                  {formatFileName(file.name)}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveImage(index)}
                  className="text-xs font-medium text-terracotta-600 hover:text-terracotta-700"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isProcessing && (
        <div className="flex items-center gap-3 rounded-xl border border-cream-200 bg-cream-50 px-4 py-3 text-sm text-brown-800">
          <ArrowPathIcon className="h-5 w-5 animate-spin text-terracotta-500" />
          <div>
            <Typography variant="subtitle2" className="text-brown-800">
              Processing upload…
            </Typography>
            <Typography variant="body2" className="text-brown-600">
              We’re extracting the medical data. You’ll see it in the record soon.
            </Typography>
          </div>
        </div>
      )}

      {!isProcessing &&
        !fileUploading &&
        !files.length &&
        lastUploadedFiles.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-beige-200 bg-white px-4 py-3 text-sm text-brown-800 shadow-sm">
          <FileUpIcon className="h-5 w-5 text-terracotta-500" />
          <div className="flex flex-col">
            <Typography variant="subtitle2" className="text-brown-800">
              Upload received
            </Typography>
            <Typography variant="body2" className="text-brown-600">
              These files will appear once processing finishes.
            </Typography>
          </div>
          <Link
            to="/files"
            className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-terracotta-600 hover:text-terracotta-700"
          >
            View file library
            <LinkIcon className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  );
}

export default function UploadDocumentButton(props: UploadDocumentButtonProps) {
  return (
    <FileUploadContextWrapper>
      <UploadDocumentButtonInner {...props} />
    </FileUploadContextWrapper>
  );
}
